/**
 * Planta resumida de un proyecto para la miniatura del inicio (paso 1.5, paridad con el v6): los
 * marcos, la huella de cada elemento, las vías, los pisos y los muros, en metros y con un decimal.
 * Tolera documentos guardados con esquemas anteriores (sin pisos ni muros).
 */

import type { Proyecto, Punto } from '../nucleo/dominio/espacio';
import { huella } from '../nucleo/espacial/geometria';

export interface PlantaResumida {
  marcos: Punto[][];
  elementos: { catalogoId: string; puntos: Punto[] }[];
  vias: { peatonal: boolean; puntos: Punto[] }[];
  pisos: Punto[][];
  muros: Punto[][];
}

const r = (v: number) => Math.round(v * 10) / 10;
const punto = (p: Punto): Punto => ({ x: r(p.x), z: r(p.z) });
const lista = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

export function plantaResumida(p: Proyecto): PlantaResumida {
  const planta: PlantaResumida = { marcos: [], elementos: [], vias: [], pisos: [], muros: [] };
  for (const e of lista<Proyecto['espacios'][number]>(p.espacios)) {
    // El marco se guarda por su centro.
    const m = e.marco;
    const [x0, x1, z0, z1] = [
      m.x - m.ancho / 2,
      m.x + m.ancho / 2,
      m.z - m.fondo / 2,
      m.z + m.fondo / 2,
    ];
    planta.marcos.push(
      [
        { x: x0, z: z0 },
        { x: x1, z: z0 },
        { x: x1, z: z1 },
        { x: x0, z: z1 },
      ].map(punto),
    );
    const l = e.layout;
    for (const x of lista<(typeof l.elementos)[number]>(l.elementos))
      planta.elementos.push({ catalogoId: x.catalogoId, puntos: huella(x).map(punto) });
    for (const v of lista<(typeof l.vias)[number]>(l.vias))
      planta.vias.push({ peatonal: v.tipo === 'peatonal', puntos: v.puntos.map(punto) });
    for (const f of lista<(typeof l.pisos)[number]>(l.pisos))
      planta.pisos.push(f.puntos.map(punto));
    for (const f of lista<(typeof l.muros)[number]>(l.muros))
      planta.muros.push(f.puntos.map(punto));
  }
  return planta;
}
